import React, { useState } from 'react';
import { Alert, Box, Button, Link, TextField, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import SectionWrapper from '../components/SectionWrapper';

// Independent from ContactPage: no lead tracking, draft storage or direct WhatsApp request.
export default function EthicsPage() {
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [reference, setReference] = useState('');
  const change = event => setForm(current => ({ ...current, [event.target.name]: event.target.value }));

  const submit = async event => {
    event.preventDefault();
    if (sending) return;
    setError('');
    setSending(true);
    try {
      const response = await fetch('/api/ethics-reports', {
        method: 'POST', credentials: 'omit',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok || !result.reference) {
        throw new Error(response.status === 429
          ? 'Limite de tentativas atingido. Aguarde e tente novamente.'
          : 'Não foi possível confirmar o envio. Seu texto foi preservado; tente novamente mais tarde.');
      }
      setReference(result.reference);
      setForm({ name: '', email: '', message: '' });
    } catch (failure) {
      setError(failure.message || 'Falha de conexão. Seu texto foi preservado.');
    } finally {
      setSending(false);
    }
  };

  return (
    <SectionWrapper id="ethics-channel" mobileSafe sx={{ bgcolor: '#fff', color: '#050505' }}>
      <Box sx={{ width: '100%', maxWidth: 760, mx: 'auto', boxSizing: 'border-box', px: { xs: 3, md: 5 }, py: { xs: 4, md: 6 } }}>
        <Typography component="h1" variant="h3" sx={{ fontWeight: 800, mb: 2 }}>Canal de Ética</Typography>
        <Typography sx={{ mb: 2 }}>
          Relate possíveis violações ao Código de Ética, irregularidades ou condutas incompatíveis com as políticas da R5.
          Este canal atende colaboradores e público externo.
        </Typography>
        <Alert severity="info" sx={{ mb: 3 }}>
          Você não precisa se identificar. Nome e e-mail são opcionais.
          Sem um contato informado, não será possível responder diretamente a você.
          Não inclua dados pessoais desnecessários no relato.
        </Alert>
        <Typography sx={{ mb: 3 }}>
          O relato é encaminhado à caixa restrita configurada para o Canal de Ética, separadamente dos contatos comerciais.
          {' '}A Presidência também pode receber, pelo WhatsApp, um aviso com a referência e a data de recebimento,
          sem o conteúdo do relato ou a identificação de quem o enviou.
          O tratamento segue a{' '}
          <Link component={RouterLink} to="/governanca/whistleblowing-handling">Política de Tratamento de Denúncias</Link>,
          que prevê confidencialidade e proteção contra retaliação de boa-fé.
          Registros técnicos de acesso podem existir na infraestrutura do site.
        </Typography>
        {reference ? (
          <Alert severity="success" role="status">
            Relato encaminhado. Guarde a referência: <strong>{reference}</strong>.
            Esta referência identifica o envio; não é um serviço de consulta de andamento.
          </Alert>
        ) : (
          <Box component="form" onSubmit={submit} autoComplete="off" sx={{ display: 'grid', gap: 2.5 }}>
            <TextField label="Nome (opcional)" name="name" value={form.name} onChange={change}
              disabled={sending} inputProps={{ maxLength: 150 }} fullWidth />
            <TextField label="E-mail (opcional)" type="email" name="email" value={form.email}
              onChange={change} disabled={sending} inputProps={{ maxLength: 254 }} fullWidth />
            <TextField label="Relato" name="message" value={form.message} onChange={change}
              required multiline minRows={6} disabled={sending} fullWidth
              inputProps={{ maxLength: 20000 }} helperText="Descreva os fatos, datas e contexto. Até 20.000 caracteres." />
            {error && <Alert severity="error" role="alert">{error}</Alert>}
            <Button variant="contained" type="submit" disabled={sending || !form.message.trim()}
              sx={{ justifySelf: 'start', bgcolor: '#CC0000', px: 4, py: 1.5 }}>
              {sending ? 'Enviando relato…' : 'Enviar relato'}
            </Button>
          </Box>
        )}
      </Box>
    </SectionWrapper>
  );
}
